import { m } from "@/paraglide/messages.js";
import { AlertTriangle } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../ui/alert-dialog.tsx";

interface ConfirmationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	/** A plain sentence, or a body of its own (paragraphs, a list) when one is not enough. */
	description: ReactNode;
	confirmText?: string;
	cancelText?: string;
	variant?: "default" | "destructive";
	onConfirm: () => void;
	onCancel?: () => void;
	/** Keeps the confirm button disabled (e.g. until an arm delay has passed). */
	confirmDisabled?: boolean;
}

export function ConfirmationDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmText = "Confirm",
	cancelText = "Cancel",
	variant = "default",
	onConfirm,
	onCancel,
	confirmDisabled = false,
}: ConfirmationDialogProps) {
	// `submitting` drives the visible disabled state. The ref synchronously
	// blocks re-entry — a state setter alone wouldn't help because two clicks
	// in the same React batch read `submitting === false`. The ref reflects
	// the new value the moment the first handler returns.
	const [submitting, setSubmitting] = useState(false);
	const submittingRef = useRef(false);

	useEffect(() => {
		if (!open) {
			submittingRef.current = false;
			setSubmitting(false);
		}
	}, [open]);

	const handleConfirm = () => {
		if (submittingRef.current) {
			return;
		}
		submittingRef.current = true;
		setSubmitting(true);
		onConfirm();
		onOpenChange(false);
	};

	const handleCancel = () => {
		onCancel?.();
		onOpenChange(false);
	};

	return (
		<AlertDialog open={open} onOpenChange={onOpenChange}>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription render={<div />} className="space-y-3">
						{typeof description === "string" ? (
							<p>{description}</p>
						) : (
							description
						)}
						{variant === "destructive" && (
							<div className="flex items-center gap-2 text-destructive">
								<AlertTriangle className="h-4 w-4" />
								<span className="text-sm font-medium">
									{m.confirmation_cannot_be_undone()}
								</span>
							</div>
						)}
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={handleCancel}>
						{cancelText}
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={handleConfirm}
						disabled={submitting || confirmDisabled}
						className={
							variant === "destructive"
								? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
								: ""
						}
					>
						{confirmText}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

// Re-export the hook from the provider
export { useConfirmationDialog } from "@/providers/confirmation-provider.tsx";
